//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { enrollmentSchema } from "../../../schemas/common/enrollment/enrollment";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

//* Zod Import
import { z } from "zod";

const enrollmentProcessResponseSchema = z.object({
  result: z.boolean(),
  data: enrollmentSchema,
  message: z.string(),
});

const EnrollmentAPI = {
  getById: async function (id: number) {
    if (!id || id <= 0) {
      return {
        data: null,
        success: false,
        message: "ID da inscrição é obrigatório e deve ser maior que zero.",
      };
    }

    try {
      const response = await privateApi.get(`/inscricoes/processos/${id}`);

      const validatedResponse = enrollmentProcessResponseSchema.parse(response);

      return {
        data: validatedResponse.data,
        success: validatedResponse.result,
        message: validatedResponse.message,
      };
    } catch (error) {
      console.error(`Erro ao buscar inscrição com ID ${id}:`, error);

      if (error instanceof z.ZodError) {
        console.error("Schema validation error:", error.issues);
        return {
          data: null,
          success: false,
          message:
            "Estrutura de resposta da API inválida. Verifique o console para detalhes.",
        };
      }

      return {
        data: null,
        success: false,
        message:
          "Não foi possível carregar os detalhes da inscrição. Tente novamente mais tarde.",
      };
    }
  },
};

export function useGetEnrollmentProcessById(id: number | null) {
  return useQuery({
    queryKey: ["enrollmentProcess", id],
    queryFn: async () => {
      if (!id || id <= 0) {
        throw new Error(
          "ID da inscrição é obrigatório e deve ser maior que zero."
        );
      }
      return await EnrollmentAPI.getById(id);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("ID da inscrição")) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    enabled: !!id && id > 0,
  });
}
